import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import GranjaArticuloCodigoField from '../../components/granja/GranjaArticuloCodigoField';
import GranjaRecetaCodigoField from '../../components/granja/GranjaRecetaCodigoField';
import granjaService, {
  GranjaArticuloClasificacion,
  GranjaConfig,
  GranjaLoteConfig,
} from '../../services/granjaService';
import { toLocalDateStringYMD } from '../../utils/formatters';
import { saveGranjaConfig } from '../../utils/granjaConfigPersist';

const emptyLote = (): GranjaLoteConfig => ({
  codigo: '',
  articuloAves: '',
  articuloHuevoBruto: '',
  fechaIngreso: toLocalDateStringYMD(),
  cabezasIniciales: 0,
  toleranciaPct: 0.5,
  activo: true,
});

const emptyArt = (): GranjaArticuloClasificacion => ({
  calibre: '',
  color: '',
  codigoArticulo: '',
});

const sectionSx = {
  p: { xs: 1.5, sm: 2 },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: '#fafafa',
};

const SectionHeader: React.FC<{
  title: string;
  actionLabel: string;
  onAction: () => void;
}> = ({ title, actionLabel, onAction }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    <Button size="small" startIcon={<AddIcon />} onClick={onAction} sx={{ textTransform: 'none' }}>
      {actionLabel}
    </Button>
  </Stack>
);

const GranjaConfiguracion: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [depositoGranja, setDepositoGranja] = useState('');
  const [articuloCascado, setArticuloCascado] = useState('');
  const [recetaMixer, setRecetaMixer] = useState('');
  const [batchKg, setBatchKg] = useState('8000');
  const [lotes, setLotes] = useState<GranjaLoteConfig[]>([]);
  const [arts, setArts] = useState<GranjaArticuloClasificacion[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const cfg = await granjaService.getConfig();
        setDepositoGranja(cfg.depositoGranja != null ? String(cfg.depositoGranja) : '');
        setArticuloCascado(cfg.articuloCascado ?? '');
        setRecetaMixer(cfg.recetaMixerDefault ?? '');
        setBatchKg(cfg.batchKgDefault != null ? String(cfg.batchKgDefault) : '8000');
        setLotes(cfg.lotes?.length ? cfg.lotes : []);
        setArts(cfg.articulosClasificacion?.length ? cfg.articulosClasificacion : []);
      } catch {
        enqueueSnackbar('Error al cargar configuración de granja', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [enqueueSnackbar]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const config: GranjaConfig = {
        depositoGranja: depositoGranja.trim() ? Number(depositoGranja) : null,
        articuloCascado: articuloCascado.trim() || null,
        recetaMixerDefault: recetaMixer.trim() || null,
        batchKgDefault: batchKg.trim() ? Number(batchKg) : null,
        lotes: lotes.filter((l) => l.codigo.trim()),
        articulosClasificacion: arts.filter((a) => a.codigoArticulo.trim()),
      };
      await saveGranjaConfig(config);
      enqueueSnackbar('Configuración guardada', { variant: 'success' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al guardar';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateLote = (idx: number, patch: Partial<GranjaLoteConfig>) => {
    setLotes((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const updateArt = (idx: number, patch: Partial<GranjaArticuloClasificacion>) => {
    setArts((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  Configuración Granja
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Parámetros operativos del módulo (SECCION=GRANJA).
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading || saving}
                sx={{ minWidth: 140, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </Stack>

            <Paper elevation={0} sx={sectionSx}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                General
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Depósito"
                    size="small"
                    value={depositoGranja}
                    onChange={(e) => setDepositoGranja(e.target.value)}
                    fullWidth
                    disabled={loading}
                    helperText="Nro. ubicación granja"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Batch kg"
                    size="small"
                    value={batchKg}
                    onChange={(e) => setBatchKg(e.target.value)}
                    fullWidth
                    disabled={loading}
                    helperText="Batch default del mixer"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GranjaArticuloCodigoField
                    label="Cascado"
                    value={articuloCascado}
                    disabled={loading}
                    dialogTitle="Artículo cascado"
                    onChange={setArticuloCascado}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GranjaRecetaCodigoField
                    label="Receta mixer"
                    value={recetaMixer}
                    disabled={loading}
                    dialogTitle="Receta mixer"
                    helperText="Receta por defecto del mixer"
                    onChange={setRecetaMixer}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <SectionHeader
                title="Lotes de aves"
                actionLabel="Agregar lote"
                onAction={() => setLotes((prev) => [...prev, emptyLote()])}
              />

              {lotes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin lotes. Agregá al menos uno para operar recolección y aves.
                </Typography>
              ) : (
                <Stack spacing={1.25} divider={<Divider flexItem />}>
                  {lotes.map((lote, idx) => (
                    <Stack
                      key={`lote-${idx}`}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <TextField
                        label="Código"
                        size="small"
                        value={lote.codigo}
                        onChange={(e) => updateLote(idx, { codigo: e.target.value })}
                        sx={{ width: { xs: '100%', md: 88 }, flexShrink: 0 }}
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                      />
                      <Box sx={{ flex: { md: '1 1 160px' }, minWidth: { md: 140 }, width: { xs: '100%', md: 'auto' } }}>
                        <GranjaArticuloCodigoField
                          label="Aves"
                          value={lote.articuloAves}
                          dialogTitle="Artículo aves"
                          onChange={(codigo) => updateLote(idx, { articuloAves: codigo })}
                        />
                      </Box>
                      <Box sx={{ flex: { md: '1 1 160px' }, minWidth: { md: 140 }, width: { xs: '100%', md: 'auto' } }}>
                        <GranjaArticuloCodigoField
                          label="Huevo bruto"
                          value={lote.articuloHuevoBruto}
                          dialogTitle="Artículo huevo bruto"
                          onChange={(codigo) => updateLote(idx, { articuloHuevoBruto: codigo })}
                        />
                      </Box>
                      <TextField
                        label="Ingreso"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={lote.fechaIngreso?.slice(0, 10) ?? ''}
                        onChange={(e) => updateLote(idx, { fechaIngreso: e.target.value })}
                        sx={{ width: { xs: '100%', sm: '48%', md: 140 }, flexShrink: 0 }}
                      />
                      <TextField
                        label="Cabezas"
                        type="number"
                        size="small"
                        value={lote.cabezasIniciales}
                        onChange={(e) =>
                          updateLote(idx, { cabezasIniciales: Number(e.target.value) || 0 })
                        }
                        sx={{ width: { xs: '48%', md: 96 }, flexShrink: 0 }}
                      />
                      <TextField
                        label="Tol. %"
                        type="number"
                        size="small"
                        value={lote.toleranciaPct}
                        onChange={(e) =>
                          updateLote(idx, { toleranciaPct: Number(e.target.value) || 0 })
                        }
                        sx={{ width: { xs: '48%', md: 88 }, flexShrink: 0 }}
                      />
                      <FormControlLabel
                        sx={{ m: 0, flexShrink: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={lote.activo}
                            onChange={(e) => updateLote(idx, { activo: e.target.checked })}
                          />
                        }
                        label={<Typography variant="body2">Activo</Typography>}
                      />
                      <Tooltip title="Eliminar lote">
                        <IconButton
                          size="small"
                          aria-label="Eliminar lote"
                          onClick={() => setLotes((prev) => prev.filter((_, i) => i !== idx))}
                          sx={{ ml: { md: 'auto' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <SectionHeader
                title="Artículos de clasificación"
                actionLabel="Agregar"
                onAction={() => setArts((prev) => [...prev, emptyArt()])}
              />

              {arts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin artículos de calibre/color. Agregá los productos clasificados.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {arts.map((art, idx) => (
                    <Stack
                      key={`art-${idx}`}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <TextField
                        label="Calibre"
                        size="small"
                        value={art.calibre}
                        onChange={(e) => updateArt(idx, { calibre: e.target.value })}
                        sx={{ width: { xs: '100%', sm: 100 }, flexShrink: 0 }}
                      />
                      <TextField
                        label="Color"
                        size="small"
                        value={art.color}
                        onChange={(e) => updateArt(idx, { color: e.target.value })}
                        sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                        <GranjaArticuloCodigoField
                          label="Artículo"
                          value={art.codigoArticulo}
                          dialogTitle="Artículo de clasificación"
                          onChange={(codigo) => updateArt(idx, { codigoArticulo: codigo })}
                        />
                      </Box>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          aria-label="Eliminar artículo"
                          onClick={() => setArts((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading || saving}
                sx={{ minWidth: 140 }}
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GranjaConfiguracion;
